import React, { memo, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionActions from '@mui/material/AccordionActions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import CancelIcon from '@mui/icons-material/Cancel';
import SvgIcon from '@mui/material/SvgIcon';
import Chip from '@mui/material/Chip';

import {ReactComponent as WaitIcon} from '../styles/Timer3.svg';

import clsx from 'clsx';
import useStyles from '../styles/Styles'

import _ from 'lodash';
import { isTrue, maybeWithPrefix, describeDate } from '../utils';
import { describeTutor, describeVenue, outputField, getCourseSchedule } from '../utils/courseUtilities';

import { addToCart, deleteFromCart, resetErrorMessage } from '../actions';

import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const CoursePanel = memo(({course, sortHeader, fieldNames, courseLabel, courseCapLabel, categoryLabel, frequency, type, tutor, tutor2, venue,
	userCanEnrol, enrolmentCounts, unVaxxed, displayTutor, displayTutor2, displayAdditionalDetail, fullVaxLabel}) => {
	
	const dispatch = useDispatch();
	const coursesSettings = useSelector(state => state.settings.courses.data);
	const enrolmentsSettings = useSelector(state => state.settings.enrolments.data);
	const courseCategories = useSelector(state => state.settings.course_categories.data);
	const cart = useSelector(state => state.cart.data);
	const enrolments = useSelector(state => state.enrolments.data);
	const message = useSelector(state => state.cart.message);
	//const cartSubmitting = useSelector(state => state.cart.submitting);
	
	const classes = useStyles();

	const category_description = _.has(courseCategories, course.category_code) ? courseCategories[course.category_code].description : course.category_code;
	const type_desciption = _.isObject(type) ? type.description : course.type_code;
	
	const [submitting, setSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);

	const dispatchAdd = () => {
		setSubmitting(true);
		setErrorMessage(false);
		dispatch(resetErrorMessage());
		dispatch(addToCart(course.code));
	}
	
	const dispatchDelete = () => {
		setSubmitting(true);
		setErrorMessage(false);
		dispatch(resetErrorMessage());
		dispatch(deleteFromCart(course.code));
	}
	
	const dispatchReset = () => {
		//setSubmitting(true);
		setErrorMessage(false);
		dispatch(resetErrorMessage());
	}
	
	useEffect(() => {
		setSubmitting(false);
	}, [cart]);
	
	// errorMessage is not a dependency; only want to run this when submitting or message change
	useEffect(() => {
		if(submitting && !_.isEmpty(message)){
			setSubmitting(false);
			setErrorMessage(message);
		} else if(!submitting && !_.isEmpty(errorMessage) && _.isEmpty(message)){
			setErrorMessage(false);
		} // eslint-disable-next-line
	}, [submitting, message])
	
	const statusMap = {
		E: 'Enrolled',
		W: 'Waitlisted',
		C: 'In cart',
		D: 'Deleted'
	}
	
	
	// <SvgIcon component={StarIcon} viewBox="0 0 600 476.6" />
	
	const getIcons = (status, is_closed, available, max_no_student, is_new) => {
		
		if(status){
			return <Chip component='span' className={classes['chip' + status]}
				label={statusMap[status]}
				avatar={<Avatar className={classes['avatar' + status]} component='span'>{status}</Avatar>}
			/>
		} else if(is_closed){
			return <Chip component='span' className={classes.chipClosed}
				label='Closed'
				icon={<LockIcon/>}
			/>
		} else if(max_no_student === 0){
			return <Chip component='span' className={classes.chipClosed}
				label='All Wait'
				icon={<SvgIcon component={WaitIcon} />}
			/>
		} else if(available < 1){
			return <Chip component='span' className={classes.chipClosed}
				label='Full'
				icon={<CancelIcon/>}
			/>
		} else if(is_new){
			return <Chip component='span' className={classes.chipNew}
				label='New'
				icon={<StarIcon/>}
			/>
		} else {
			return <span></span>
		}
		
	}
	
	const describeDateRange = (is_finished, start_date, end_date) => {
		if(is_finished){
			return <><strong>Finished</strong> on {describeDate(end_date)}</>;
		} else if(start_date === end_date){
			return describeDate(start_date);
		} else {
			return <>{describeDate(start_date)} - {describeDate(end_date)}</>
		}
		
	}
	
	let courseIsAvailable = false;
	let describeCourseAvailability = 'No';
	
	if(!enrolmentsSettings.general_enrol_on && !enrolmentsSettings.early_enrol_on){
		describeCourseAvailability = 'No: All enrolments are currently off';
	} else if(isTrue(course.is_finished)){
		describeCourseAvailability = 'No: ' + courseCapLabel + ' is finished';
	} else if(dayjs(course.start_date).isSameOrBefore(coursesSettings.member_courses_end_date) &&
			dayjs(course.end_date).isSameOrAfter(coursesSettings.member_courses_start_date)){
		if(isTrue(course.is_closed)){
			describeCourseAvailability = 'No: ' + courseCapLabel + ' is closed to enrolments';
		} else {
			courseIsAvailable = true;
			if(enrolmentsSettings.early_enrol_on){
				describeCourseAvailability = 'Yes: Early enrolments only';
			} else if(course.max_no_student === 0){
				describeCourseAvailability = 'Yes: All enrolments will be waitlisted pending admission.';
			} else if(course.available < 1) {
				describeCourseAvailability = 'Yes: Waitlist enrolments only';
			} else {
				describeCourseAvailability = 'Yes';
			}
		}
	}
	
	const courseAvailability = <>
	Accepting enrolments? {describeCourseAvailability}<br/>
	Capacity: {course.max_no_student}, enrolled: {course.enrolled}, available: {course.available}, waitlisted: {course.waitlisted}
	</>;
	
	let status = false;
	
	const enrolment = _.find(enrolments.enrolled, ['course_code', course.code]);
	if(enrolment){
		status = enrolment.status === 'Deleted' ? 'D' : 'E';
	} else if(_.some(enrolments.waitlisted, ['course_code', course.code])){
		status = 'W';
	} else if(_.some(cart, ['course_code', course.code])){
		status = 'C';
	}
	
	const panelActions = () => {
		
		
		let content = '';
		
		const AddButton = () => (
			<Button size="small" color="primary" variant="contained" disabled={submitting}
				onClick={dispatchAdd}
			>Add to cart
			{submitting && <CircularProgress size={24} className={classes.buttonProgress} />}
			</Button>
		); 
		
		const CloseButton = () => (
			<Button size="small" color="primary" disabled={submitting}
				onClick={e => setIsExpanded(false)}
			>Close
			</Button>
		)
		
		if((!userCanEnrol || !courseIsAvailable) && !status){
			content = ''
		} else if(errorMessage){
			content = <>
			<Typography variant="body1">{errorMessage}</Typography>
			<Button size="small" color="primary" disabled={submitting}
				onClick={dispatchReset}
			>Dismiss
			{submitting && <CircularProgress size={24} className={classes.buttonProgress} />}
			</Button>
			</>
		} else if(status === 'E'){
			content = <Typography variant="body1">You are enrolled in this {courseLabel}.</Typography>
		} else if(status === 'W'){
			content = <Typography variant="body1">You are waitlisted in this {courseLabel}.</Typography>
		} else if(status === 'D'){
			content = <Typography variant="body1">You were previously enrolled in this {courseLabel}, but your enrolment was deleted. Please <Link to="/contact" className={classes.noBreakAll}>contact us</Link> if you wish to be reinstated.</Typography>
		} else if(status === 'C'){
			content = <Button size="small" color="primary" disabled={submitting}
				onClick={dispatchDelete}
			>Remove from cart
			{submitting && <CircularProgress size={24} className={classes.buttonProgress} />}
			</Button>
		} else if(unVaxxed){
			content = <Typography variant="body1">This is a face-to-face {courseLabel}: Only {fullVaxLabel} members can enrol. If you are {fullVaxLabel}, please update your <Link to="/member" className={classes.noBreakAll}>member details</Link> accordingly.</Typography>
		} else if(enrolmentsSettings.enrolment_limits_on){
			
			// If this category has a limit and the user has reached the limit 
			if(_.has(enrolmentCounts.category_counts, course.category_code) &&
				_.has(courseCategories, course.category_code) &&
				courseCategories[course.category_code].max_enrol > 0 && 
				enrolmentCounts.category_counts[course.category_code] >= courseCategories[course.category_code].max_enrol){
					
					content = <Typography variant="body1">You can&apos;t add this {courseLabel} to your cart: You have already reached the maximum number of enrolments allowed in the {course.category_code} {categoryLabel}. {cart.length > 0 ? <>If you want to add this {courseLabel} to your cart, you need to first remove another {courseLabel} in the {course.category_code} {categoryLabel} from your cart (if possible)</> : <>You can't add this {courseLabel} to your cart at this time</>}. Enrolment maximum limits will be lifted from {describeDate(enrolmentsSettings.max_enrolments_end_date)}.</Typography>;
	
			// If the user has reached the global limt and this category is not excluded from global limit
			} else if(enrolmentsSettings.max_enrolments_allowed > 0 &&
			enrolmentCounts.counted_total >= enrolmentsSettings.max_enrolments_allowed &&
			((_.has(courseCategories, course.category_code) &&
			!courseCategories[course.category_code].exempt_from_global_max_enrol) ||
			!_.has(courseCategories, course.category_code))){
				
				content = <Typography variant="body1">You have already reached the overall maximum number of enrolments allowed. {cart.length > 0 ? <>If you want to add this {courseLabel} to your cart, you need to first remove another {courseLabel} from your cart</> : <>You can't add this {courseLabel} to your cart at this time</>}. Enrolment maximum limits will be lifted from {describeDate(enrolmentsSettings.max_enrolments_end_date)}.</Typography>;
				
			// All checks passed
			} else {
				content = <AddButton/>
			}
			
		} else {
			content = <AddButton/>
		}

		return (
		<>
		<Divider />
        <AccordionActions>
		{content}
		<CloseButton/>
        </AccordionActions>
		</>
	)}
	
	const venueDetail = <>{describeVenue(venue, course.venue_code)} {isTrue(course.distance_course) ? <><br/><strong>This is a distance {courseLabel}</strong></> : null}</>;
	
	return (
		<Accordion 
			className={clsx(classes.innerContent, classes.accordion, classes.course)}
			TransitionProps={{ unmountOnExit: true }}
			expanded={isExpanded}
		>
		<AccordionSummary
          expandIcon={<ExpandMoreIcon color="primary" />}
		  onClick={e => setIsExpanded(!isExpanded)}
        >
		<Typography // variant="body1"
		variant="subtitle2" component="h3"
		>
		{getIcons(status, course.is_closed, course.available, course.max_no_student, course.is_new)}
		{course.name}
		</Typography>
		{ sortHeader === null ? null : 
		<Typography variant="body2" className={classes.secondaryText} >
		{sortHeader}
		</Typography>
		}
		<Typography variant="body2" className={classes.secondaryText} >
		{describeDateRange(course.is_finished, course.start_date, course.end_date)}
		</Typography>
		</AccordionSummary>
		<AccordionDetails>
		<Grid container className={classes.courseDetails}>
			{course.is_new ? outputField(<strong>New!</strong>, course.name +' is a new addition to the timetable', false, classes) : null}
			{outputField(fieldNames.code, course.code, false, classes)}
			{outputField(fieldNames.category_code, maybeWithPrefix(course.category_code, category_description), false, classes)}
			{outputField(fieldNames.type_code, maybeWithPrefix(course.type_code, type_desciption ), false, classes)}
			{outputField(fieldNames.detail, course.detail, false, classes)}
			{displayAdditionalDetail ? outputField(fieldNames.additional_detail, course.additional_detail, false, classes) : null}
			{displayTutor ? outputField(fieldNames.tutor, describeTutor(tutor, course.tutor), false, classes) : null}
			{displayTutor2 ? outputField(fieldNames.tutor2, describeTutor(tutor2, course.tutor2), false, classes) : null}
			{outputField(fieldNames.venue_code, venueDetail, false, classes)}
			{outputField('Timetable', getCourseSchedule(course, frequency), false, classes)}
			{outputField('Availability', courseAvailability, false, classes)}
			{outputField(fieldNames.fee1, '$' + course.fee1, false, classes)}
		</Grid>
		</AccordionDetails>
		{panelActions()}
		</Accordion>
)})

export default CoursePanel;